import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B', purple:'#7C3AED' };

const TEST_ICONS = { ielts:'🎓', cefr:'📊', sat:'📐', presidential:'🏛️' };
const TEST_COLORS = { ielts: C.teal, cefr: C.purple, sat:'#F59E0B', presidential:'#10B981' };

export default function SuperAdminMockTests() {
  const [overview, setOverview] = useState(null);
  const [testTypes, setTestTypes] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [toggleModal, setToggleModal] = useState(null); // {tenant, enabled}
  const [toggleForm, setToggleForm] = useState({ enabled: false, addon_fee_usd: 35 });
  const [saving, setSaving] = useState(false);
  const [editType, setEditType] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [qForm, setQForm] = useState({ test_type_slug:'cefr', section:'grammar', question_type:'mcq', question_text:'', option_a:'', option_b:'', option_c:'', option_d:'', correct_answer:'A', explanation:'', cefr_level:'', difficulty:3 });
  const [bulkText, setBulkText] = useState('');
  const [qSaving, setQSaving] = useState(false);

  const addOneQuestion = async () => {
    if (!qForm.question_text || !qForm.section) { toast.error('Question text and section are required'); return; }
    setQSaving(true);
    try {
      const tt = testTypes.find(t => t.slug === qForm.test_type_slug);
      await api.post('/api/admin/mock-tests/questions', { ...qForm, test_type_id: tt?.id });
      toast.success('Question added');
      setQForm({ ...qForm, question_text:'', option_a:'', option_b:'', option_c:'', option_d:'', explanation:'' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to add'); }
    setQSaving(false);
  };

  const bulkImport = async () => {
    let arr;
    try { arr = JSON.parse(bulkText); } catch { toast.error('Invalid JSON — must be an array'); return; }
    if (!Array.isArray(arr) || !arr.length) { toast.error('Provide a non-empty JSON array of questions'); return; }
    setQSaving(true);
    try {
      const r = await api.post('/api/superadmin/mock-tests/questions/bulk', { questions: arr });
      toast.success(`Imported ${r.data.added}${r.data.failed ? `, ${r.data.failed} failed` : ''}`);
      if (r.data.failed) console.warn('Bulk import errors:', r.data.errors);
      setBulkText('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Bulk import failed'); }
    setQSaving(false);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ovr, types] = await Promise.all([
        api.get('/api/superadmin/mock-tests/overview'),
        api.get('/api/superadmin/mock-tests/types'),
      ]);
      setOverview(ovr.data);
      setTestTypes(types.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const openToggle = (tenant) => {
    setToggleModal(tenant);
    setToggleForm({ enabled: !tenant.mock_test_enabled, addon_fee_usd: tenant.addon_fee_usd || 35 });
  };

  const doToggle = async () => {
    if (!toggleModal) return;
    setSaving(true);
    try {
      await api.post('/api/superadmin/mock-tests/toggle', {
        tenant_id: toggleModal.tenant_id,
        enabled: toggleForm.enabled,
        addon_fee_usd: parseFloat(toggleForm.addon_fee_usd) || 0,
      });
      toast.success(`Mock tests ${toggleForm.enabled ? 'enabled' : 'disabled'} for ${toggleModal.tenant_name}`);
      setToggleModal(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const openEditType = (tt) => {
    setEditType(tt);
    setEditForm({ name: tt.name, description: tt.description, duration_minutes: tt.duration_minutes, default_price_usd: tt.default_price_usd });
  };

  const saveType = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/superadmin/mock-tests/types/${editType.id}`, editForm);
      toast.success('Test type updated');
      setEditType(null);
      fetchAll();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const qBank = overview?.question_bank || {};
  const tenants = overview?.tenants || [];
  const enabledCount = tenants.filter(t => t.mock_test_enabled).length;
  const totalBookings = tenants.reduce((s, t) => s + (t.total_bookings || 0), 0);
  const totalQs = Object.values(qBank).reduce((s, v) => s + v, 0);

  const tabs = ['overview', 'partners', 'test types', 'question bank'];

  return (
    <Layout role="superadmin">
      <div style={{ padding: '24px', maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: C.navy, margin: 0 }}>🧪 Mock Test Module</h1>
          <p style={{ color: C.grey, marginTop: 4, fontSize: 14 }}>Manage mock test access for partner schools, test types, and the question bank.</p>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Partners with Mock Tests', value: enabledCount, icon: '🏫', color: C.teal },
            { label: 'Total Bookings', value: totalBookings, icon: '📋', color: C.purple },
            { label: 'Total Questions', value: totalQs, icon: '❓', color: C.amber },
            { label: 'Test Types Active', value: testTypes.filter(t => t.is_active).length, icon: '📝', color: C.success },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, padding: '18px 20px' }}>
              <div style={{ fontSize: 28 }}>{s.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
              background: tab === t ? C.teal : '#fff', color: tab === t ? '#fff' : C.navy,
              border: `1px solid ${tab === t ? C.teal : C.border}`,
            }}>{t}</button>
          ))}
        </div>

        {loading && <div style={{ textAlign:'center', padding: 60, color: C.grey }}>Loading...</div>}

        {/* OVERVIEW TAB */}
        {!loading && tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {/* Question Bank Summary */}
            <div style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.navy }}>📚 Question Bank</h3>
              {Object.entries(qBank).map(([slug, count]) => (
                <div key={slug} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize: 13, color: C.navy }}>{TEST_ICONS[slug] || '📝'} {slug.toUpperCase()}</span>
                  <span style={{ fontWeight: 700, color: count >= 100 ? C.success : count >= 30 ? C.amber : C.error, fontSize: 14 }}>{count} questions</span>
                </div>
              ))}
            </div>
            {/* Default Prices */}
            <div style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: C.navy }}>💰 Default Prices</h3>
              {testTypes.map(tt => (
                <div key={tt.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize: 13, color: C.navy }}>{TEST_ICONS[tt.slug] || '📝'} {tt.name}</span>
                  <span style={{ fontWeight: 700, color: C.teal }}>${tt.default_price_usd}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PARTNERS TAB */}
        {!loading && tab === 'partners' && (
          <div style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Partner School', 'Mock Tests', 'Add-on Fee / month', 'Bookings', 'Action'].map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize: 12, fontWeight: 700, color: C.grey, textTransform:'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tenants.map((t, i) => (
                  <tr key={t.tenant_id} style={{ background: i%2===0 ? '#fff' : C.bg, borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'12px 16px', fontWeight: 600, color: C.navy, fontSize: 14 }}>{t.tenant_name}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{
                        background: t.mock_test_enabled ? '#D1FAE5' : '#F3F4F6',
                        color: t.mock_test_enabled ? C.success : C.grey,
                        fontSize: 11, fontWeight: 700, padding:'3px 10px', borderRadius: 20
                      }}>{t.mock_test_enabled ? '✓ Enabled' : 'Disabled'}</span>
                    </td>
                    <td style={{ padding:'12px 16px', color: t.addon_fee_usd > 0 ? C.navy : C.grey, fontSize: 14 }}>
                      {t.addon_fee_usd > 0 ? `$${t.addon_fee_usd}/mo` : '—'}
                    </td>
                    <td style={{ padding:'12px 16px', fontSize: 14, color: C.navy }}>{t.total_bookings}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <button onClick={() => openToggle(t)} style={{
                        padding:'6px 14px', borderRadius: 6, border:'none', cursor:'pointer', fontSize: 12, fontWeight: 600,
                        background: t.mock_test_enabled ? '#FEE2E2' : '#D1FAE5',
                        color: t.mock_test_enabled ? C.error : C.success,
                      }}>{t.mock_test_enabled ? 'Disable' : 'Enable'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TEST TYPES TAB */}
        {!loading && tab === 'test types' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {testTypes.map(tt => (
              <div key={tt.id} style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, padding: 20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 22 }}>{TEST_ICONS[tt.slug] || '📝'}</div>
                    <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginTop: 4 }}>{tt.name}</div>
                    <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>{tt.duration_minutes} min</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.teal }}>${tt.default_price_usd}</div>
                    <div style={{ fontSize: 11, color: C.grey }}>default price</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.grey, marginBottom: 12 }}>{tt.description}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap: 4, marginBottom: 14 }}>
                  {(tt.sections || []).map(s => (
                    <span key={s} style={{ background: C.bg, color: C.navy, fontSize: 10, fontWeight: 600, padding:'2px 8px', borderRadius: 12, textTransform:'capitalize' }}>{s.replace(/_/g,' ')}</span>
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize: 12, color: qBank[tt.slug] >= 50 ? C.success : C.amber, fontWeight: 600 }}>
                    {qBank[tt.slug] || 0} questions
                  </span>
                  <button onClick={() => openEditType(tt)} style={{ padding:'6px 14px', borderRadius: 6, background: C.teal, color:'#fff', border:'none', cursor:'pointer', fontSize: 12, fontWeight: 600 }}>Edit</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* QUESTION BANK TAB */}
        {!loading && tab === 'question bank' && (
          <div style={{ background:'#fff', borderRadius: 12, border:`1px solid ${C.border}`, padding: 24 }}>
            <h3 style={{ margin:'0 0 8px', color: C.navy }}>Question Bank Summary</h3>
            <p style={{ fontSize: 13, color: C.grey, marginBottom: 20 }}>Questions are managed globally. School admins can also add questions. To bulk import, use the server import scripts.</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              {testTypes.map(tt => {
                const count = qBank[tt.slug] || 0;
                const pct = Math.min(100, (count / 300) * 100);
                const color = count >= 200 ? C.success : count >= 50 ? C.amber : C.error;
                return (
                  <div key={tt.id} style={{ padding: 16, borderRadius: 10, border:`1px solid ${C.border}` }}>
                    <div style={{ fontWeight: 700, color: C.navy, marginBottom: 8 }}>{TEST_ICONS[tt.slug]} {tt.name}</div>
                    <div style={{ background: C.bg, borderRadius: 6, height: 8, marginBottom: 8 }}>
                      <div style={{ background: color, height: 8, borderRadius: 6, width: `${pct}%`, transition:'width 0.5s' }} />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize: 12 }}>
                      <span style={{ color, fontWeight: 700 }}>{count} questions</span>
                      <span style={{ color: C.grey }}>target: 300+</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Add a single question */}
            {(() => {
              const sel = testTypes.find(t => t.slug === qForm.test_type_slug);
              const sections = (sel && sel.sections) || [];
              const inp = { padding:'8px 10px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, width:'100%' };
              return (
            <div style={{ marginTop: 28, padding: 20, background:'#fff', border:`1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin:'0 0 14px', color: C.navy, fontSize:15 }}>➕ Add a Question</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10, marginBottom:10 }}>
                <select value={qForm.test_type_slug} style={inp}
                  onChange={e => { const s = testTypes.find(t=>t.slug===e.target.value); setQForm(f => ({ ...f, test_type_slug:e.target.value, section:(s&&s.sections&&s.sections[0])||'' })); }}>
                  {testTypes.map(t => <option key={t.id} value={t.slug}>{t.name}</option>)}
                </select>
                <select value={qForm.section} style={inp} onChange={e => setQForm(f => ({ ...f, section:e.target.value }))}>
                  {sections.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={qForm.correct_answer} style={inp} onChange={e => setQForm(f => ({ ...f, correct_answer:e.target.value }))}>
                  {['A','B','C','D'].map(x => <option key={x} value={x}>Correct: {x}</option>)}
                </select>
                <select value={qForm.cefr_level} style={inp} onChange={e => setQForm(f => ({ ...f, cefr_level:e.target.value }))}>
                  <option value="">CEFR: n/a</option>
                  {['A1','A2','B1','B2','C1','C2'].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <textarea placeholder="Question text" value={qForm.question_text} style={{ ...inp, minHeight:54, marginBottom:10 }} onChange={e => setQForm(f => ({ ...f, question_text:e.target.value }))} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                {['a','b','c','d'].map(o => (
                  <input key={o} placeholder={`Option ${o.toUpperCase()}`} value={qForm['option_'+o]} style={inp} onChange={e => setQForm(f => ({ ...f, ['option_'+o]:e.target.value }))} />
                ))}
              </div>
              <input placeholder="Explanation (optional)" value={qForm.explanation} style={{ ...inp, marginBottom:10 }} onChange={e => setQForm(f => ({ ...f, explanation:e.target.value }))} />
              <button onClick={addOneQuestion} disabled={qSaving} style={{ padding:'9px 18px', borderRadius:8, background:C.teal, color:'#fff', border:'none', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                {qSaving ? 'Saving…' : 'Add Question'}
              </button>
            </div>
              );
            })()}

            {/* Bulk import */}
            <div style={{ marginTop: 20, padding: 20, background:'#fff', border:`1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin:'0 0 6px', color: C.navy, fontSize:15 }}>⬆ Bulk Upload (JSON)</h3>
              <p style={{ fontSize:12, color:C.grey, margin:'0 0 10px' }}>
                Paste a JSON array. Each item needs <code>test_type_slug</code> (ielts/cefr/sat/presidential), <code>section</code>, <code>question_text</code>, <code>option_a…d</code>, <code>correct_answer</code> (A–D), and optionally <code>explanation</code>, <code>cefr_level</code>, <code>difficulty</code>.
              </p>
              <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                placeholder={'[\n  {"test_type_slug":"cefr","section":"grammar","question_text":"She ___ here.","option_a":"work","option_b":"works","option_c":"working","option_d":"worked","correct_answer":"B","cefr_level":"A2"}\n]'}
                style={{ width:'100%', minHeight:140, fontFamily:'monospace', fontSize:12, padding:12, borderRadius:8, border:`1px solid ${C.border}` }} />
              <button onClick={bulkImport} disabled={qSaving} style={{ marginTop:10, padding:'9px 18px', borderRadius:8, background:C.purple, color:'#fff', border:'none', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                {qSaving ? 'Importing…' : 'Import Questions'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TOGGLE MODAL */}
      {toggleModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 999 }}>
          <div style={{ background:'#fff', borderRadius: 16, padding: 32, width: 420, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 6px', color: C.navy, fontSize: 18 }}>
              {toggleForm.enabled ? '✅ Enable' : '🚫 Disable'} Mock Tests
            </h3>
            <p style={{ color: C.grey, fontSize: 13, marginBottom: 20 }}>
              {toggleForm.enabled ? `Enable the Mock Test module for ${toggleModal.tenant_name}. They will gain access to manage slots, bookings, and issue certificates.`
                : `Disable mock tests for ${toggleModal.tenant_name}. Existing bookings and results will be preserved.`}
            </p>
            {toggleForm.enabled && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display:'block', marginBottom: 6 }}>Monthly Add-on Fee (USD)</label>
                <input type="number" min="0" step="5" value={toggleForm.addon_fee_usd}
                  onChange={e => setToggleForm(f => ({...f, addon_fee_usd: e.target.value}))}
                  style={{ width:'100%', padding:'10px 12px', borderRadius: 8, border:`1px solid ${C.border}`, fontSize: 14, boxSizing:'border-box' }} />
                <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>This is added to the school's monthly invoice. Set to 0 for free.</div>
              </div>
            )}
            <div style={{ display:'flex', gap: 12, justifyContent:'flex-end' }}>
              <button onClick={() => setToggleModal(null)} style={{ padding:'10px 20px', borderRadius: 8, border:`1px solid ${C.border}`, background:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={doToggle} disabled={saving} style={{ padding:'10px 24px', borderRadius: 8, border:'none', background: toggleForm.enabled ? C.success : C.error, color:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>
                {saving ? 'Saving...' : toggleForm.enabled ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT TYPE MODAL */}
      {editType && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 999 }}>
          <div style={{ background:'#fff', borderRadius: 16, padding: 32, width: 480, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 20px', color: C.navy }}>Edit {editType.name}</h3>
            {[
              { label:'Name', key:'name', type:'text' },
              { label:'Duration (minutes)', key:'duration_minutes', type:'number' },
              { label:'Default Price (USD)', key:'default_price_usd', type:'number' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display:'block', marginBottom: 6 }}>{f.label}</label>
                <input type={f.type} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({...p, [f.key]: e.target.value}))}
                  style={{ width:'100%', padding:'10px 12px', borderRadius: 8, border:`1px solid ${C.border}`, fontSize: 14, boxSizing:'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display:'block', marginBottom: 6 }}>Description</label>
              <textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({...p, description: e.target.value}))} rows={3}
                style={{ width:'100%', padding:'10px 12px', borderRadius: 8, border:`1px solid ${C.border}`, fontSize: 13, boxSizing:'border-box', resize:'vertical' }} />
            </div>
            <div style={{ display:'flex', gap: 12, justifyContent:'flex-end' }}>
              <button onClick={() => setEditType(null)} style={{ padding:'10px 20px', borderRadius: 8, border:`1px solid ${C.border}`, background:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={saveType} disabled={saving} style={{ padding:'10px 24px', borderRadius: 8, border:'none', background: C.teal, color:'#fff', cursor:'pointer', fontSize: 13, fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
